import { useMemo } from "react";
import { Box, Chip, Dialog, DialogContent, DialogTitle, IconButton, Stack, Typography } from "@mui/material";
import { Close as CloseIcon, ErrorOutline as ErrorIcon } from "@mui/icons-material";
import { TargetSchema, type DocumentTransformation, type TransformationTemplate } from "@/types";
import type { ExtractedRecordError } from "@/types/documentTransformation";
import { DATE_FORMATS, formatDate } from "@/utils/formatters/dateFormatter";
import { ENTITY_CONFIG } from "../result-tab/config";
import { formatRecordType } from "../../utils/documentUtils";
import type { AppliedSchemaMapping } from "../../hooks/useAppliedFieldMapping";
import { buildHeaderCoverage } from "./buildHeaderCoverage";
import { formatDocumentSubtype, formatExtractionMethod, formatProcessingTime } from "./transformationDisplay";

interface TransformationDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  transformation: DocumentTransformation | null;
  template: TransformationTemplate | null | undefined;
  schemaMappings: AppliedSchemaMapping[];
}

/**
 * Self-contained transformation-details surface for the Mapping tab.
 * Replaces the deleted `/document-transformations/{id}/show` route — that
 * page rendered the raw resource (UUIDs, collapsed JSON arrays, raw enum
 * values) and was unfriendly to common users.
 *
 * Sections (each renders only when its data is present, so clean docs
 * compress and problematic docs expand):
 *
 *   - Header: status, run timestamp, template name, sub-type
 *   - Coverage by schema: per-schema field counts + names of unpopulated
 *     fields with per-record miss counts
 *   - Validation errors: grouped by schema (only when present)
 *   - Detected headers: full mapped + unmapped chip lists
 *   - Run details: extraction method, pages, processing time, AI model,
 *     sheet name, records extracted
 *
 * Per-record extraction details are deliberately not surfaced here — the
 * Result tab already lists the loaded records, and duplicating them in
 * pre-load form would compete with that view. Per-record validation
 * errors flatten up into the transformation-level Validation errors
 * section above.
 *
 * No escape hatch to a raw resource view — the dialog is the canonical
 * surface for transformation details now.
 */
export const TransformationDetailsDialog = ({
  open,
  onClose,
  transformation,
  template,
  schemaMappings,
}: TransformationDetailsDialogProps) => {
  const meta = transformation?.processing_metadata;
  const coverage = transformation?.coverage_analysis;
  const validationErrors = transformation?.validation_errors ?? [];

  const hasTemplate = !!template?.name;
  const subtype = formatDocumentSubtype(meta?.document_type);
  const headerCoverage = useMemo(
    () => buildHeaderCoverage(meta?.detected_headers, schemaMappings),
    [meta?.detected_headers, schemaMappings]
  );

  // Group validation errors by schema for readability.
  const errorsBySchema = useMemo(() => {
    const map = new Map<TargetSchema | "_unscoped", ExtractedRecordError[]>();
    for (const err of validationErrors) {
      const key = err.schema ?? "_unscoped";
      const list = map.get(key) ?? [];
      list.push(err);
      map.set(key, list);
    }
    return map;
  }, [validationErrors]);

  // Friendly counts for the per-schema coverage breakdown header.
  //
  // Prefer the server-computed summary (#6755): extracted_records is no longer
  // fetched on the review path, so reading its length here would silently
  // display 0. Falls back to the array for transformations written before the
  // summary existed, and for callers that still pass a fully-expanded row.
  const recordCount =
    transformation?.processing_metadata?.record_summary?.total_records ??
    transformation?.extracted_records?.length ??
    0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ pr: 6 }}>
        Transformation details
        <IconButton aria-label="Close" onClick={onClose} size="small" sx={{ position: "absolute", right: 12, top: 12 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {/* Header: status + when ran + template */}
          <Section>
            <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ rowGap: 0.5 }}>
              {transformation && (
                <Chip
                  label={transformation.status}
                  size="small"
                  color={transformation.status === "completed" ? "success" : "default"}
                  sx={{ textTransform: "capitalize", fontWeight: 500 }}
                />
              )}
              {transformation?.created_datetime && (
                <Typography variant="body2" color="text.secondary">
                  Ran {formatDate(transformation.created_datetime, DATE_FORMATS.DISPLAY_LONG_WITH_TIME)}
                </Typography>
              )}
            </Stack>
            <Stack direction="row" alignItems="baseline" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {hasTemplate ? template?.name : "Automatic extraction"}
              </Typography>
              {hasTemplate && template?.version && (
                <Typography variant="body2" color="text.secondary">
                  v{template.version}
                </Typography>
              )}
            </Stack>
            {subtype && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {subtype}
              </Typography>
            )}
          </Section>

          {/* Per-schema coverage breakdown — the headline gap-vs-show-page value. */}
          {coverage?.schema_analyses && coverage.schema_analyses.length > 0 && (
            <Section title="Coverage by schema">
              <Stack spacing={1.5}>
                {coverage.schema_analyses.map((analysis) => (
                  <SchemaCoverageRow key={analysis.target_schema} analysis={analysis} recordCount={recordCount} />
                ))}
              </Stack>
            </Section>
          )}

          {/* Validation errors — prominent only when present. */}
          {validationErrors.length > 0 && (
            <Section
              title={`Validation errors (${validationErrors.length})`}
              titleColor="error.main"
              titleIcon={<ErrorIcon color="error" fontSize="small" />}
            >
              <Stack spacing={1.5}>
                {[...errorsBySchema.entries()].map(([schemaKey, errors]) => (
                  <Box key={String(schemaKey)}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}
                    >
                      {schemaKey === "_unscoped" ? "General" : (ENTITY_CONFIG[schemaKey]?.label ?? String(schemaKey))}
                    </Typography>
                    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                      {errors.map((err, i) => (
                        <ValidationErrorRow key={`${String(schemaKey)}-${i}`} error={err} />
                      ))}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </Section>
          )}

          {/* Detected headers — mapped + unmapped, full lists. */}
          {(headerCoverage.mapped.length > 0 || headerCoverage.unmapped.length > 0) && (
            <Section title="Detected headers">
              {headerCoverage.mapped.length > 0 && (
                <Box sx={{ mb: headerCoverage.unmapped.length > 0 ? 1.5 : 0 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}
                  >
                    Mapped ({headerCoverage.mapped.length})
                  </Typography>
                  <ChipList items={headerCoverage.mapped} variant="default" />
                </Box>
              )}
              {headerCoverage.unmapped.length > 0 && (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}
                  >
                    Unmapped ({headerCoverage.unmapped.length})
                  </Typography>
                  <ChipList items={headerCoverage.unmapped} variant="warning" />
                </Box>
              )}
            </Section>
          )}

          {/* Run details — extraction method, pages, time, AI model, sheet. */}
          <Section title="Run details">
            <Stack spacing={1}>
              {meta?.extraction_method && (
                <KeyValueRow
                  label="Extraction method"
                  value={formatExtractionMethod(meta.extraction_method) ?? meta.extraction_method}
                />
              )}
              {meta?.document_pages !== undefined && meta.document_pages !== null && (
                <KeyValueRow label="Pages" value={String(meta.document_pages)} />
              )}
              {meta?.processing_time_ms !== undefined && (
                <KeyValueRow
                  label="Processing time"
                  value={formatProcessingTime(meta.processing_time_ms) ?? "—"}
                  hint={`${meta.processing_time_ms.toLocaleString()} ms`}
                />
              )}
              {meta?.ai_model_version && <KeyValueRow label="AI model" value={meta.ai_model_version} />}
              {meta?.process_sheet_name && <KeyValueRow label="Sheet" value={meta.process_sheet_name} />}
              {recordCount > 0 && <KeyValueRow label="Records extracted" value={String(recordCount)} />}
            </Stack>
          </Section>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Section primitives
// ---------------------------------------------------------------------------

interface SectionProps {
  title?: string;
  titleColor?: string;
  titleIcon?: React.ReactNode;
  children: React.ReactNode;
}

const Section = ({ title, titleColor, titleIcon, children }: SectionProps) => (
  <Box>
    {title && (
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
        {titleIcon}
        <Typography variant="subtitle2" fontWeight={600} sx={{ color: titleColor ?? "text.primary" }}>
          {title}
        </Typography>
      </Stack>
    )}
    {children}
  </Box>
);

interface SchemaCoverageRowProps {
  analysis: NonNullable<DocumentTransformation["coverage_analysis"]>["schema_analyses"][number];
  recordCount: number;
}

const SchemaCoverageRow = ({ analysis, recordCount }: SchemaCoverageRowProps) => {
  const totalFields = analysis.expected_fields.length;
  const unpopulated = analysis.unpopulated_fields ?? {};
  const unpopulatedFieldNames = Object.keys(unpopulated);
  const populatedCount = Math.max(0, totalFields - unpopulatedFieldNames.length);
  const schemaConfig = ENTITY_CONFIG[analysis.target_schema];

  return (
    <Box
      sx={{
        p: 1.5,
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
        <Typography variant="body2" fontWeight={600}>
          {schemaConfig?.labelPlural ?? String(analysis.target_schema)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {populatedCount} of {totalFields} field{totalFields !== 1 ? "s" : ""} populated
        </Typography>
      </Stack>
      {unpopulatedFieldNames.length > 0 ? (
        <Stack spacing={0.25} sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Unpopulated fields:
          </Typography>
          {unpopulatedFieldNames.map((field) => {
            const count = unpopulated[field];
            const showCount = recordCount > 0 && count > 0;
            return (
              <Typography key={field} variant="caption" component="div" sx={{ pl: 1 }}>
                • {formatRecordType(field)}
                {showCount && (
                  <Box component="span" sx={{ color: "text.secondary", ml: 0.5 }}>
                    (missing on {count} of {recordCount} record{recordCount !== 1 ? "s" : ""})
                  </Box>
                )}
              </Typography>
            );
          })}
        </Stack>
      ) : (
        <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: "block" }}>
          All fields populated.
        </Typography>
      )}
    </Box>
  );
};

const ValidationErrorRow = ({ error }: { error: ExtractedRecordError }) => (
  <Box sx={{ pl: 1 }}>
    <Typography variant="body2">
      {error.field && (
        <Box component="span" sx={{ fontWeight: 500 }}>
          {formatRecordType(error.field)}:{" "}
        </Box>
      )}
      {error.message}
    </Typography>
    {error.rule && (
      <Typography variant="caption" color="text.secondary">
        Rule: {error.rule}
      </Typography>
    )}
  </Box>
);

interface ChipListProps {
  items: string[];
  variant: "default" | "warning";
}

const ChipList = ({ items, variant }: ChipListProps) => (
  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.75 }}>
    {items.map((item) => (
      <Chip
        key={item}
        label={item}
        size="small"
        variant="outlined"
        color={variant === "warning" ? "warning" : "default"}
        sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}
      />
    ))}
  </Box>
);

interface KeyValueRowProps {
  label: string;
  value: string;
  hint?: string;
}

const KeyValueRow = ({ label, value, hint }: KeyValueRowProps) => (
  <Stack direction="row" justifyContent="space-between" alignItems="baseline">
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Stack direction="row" spacing={0.75} alignItems="baseline">
      <Typography variant="body2" fontWeight={500}>
        {value}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary">
          ({hint})
        </Typography>
      )}
    </Stack>
  </Stack>
);
